import React from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function Rating({ value }) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n) || n <= 0) return <span className="text-gray-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-semibold text-gray-800">
      {n}
      <span className="text-gray-400 text-xs">/ 5</span>
    </span>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="mt-1 text-sm text-gray-900">{children ?? <span className="text-gray-300">—</span>}</div>
    </div>
  );
}

function IssuesList({ items, columns }) {
  const nonEmpty = (items || []).filter(i =>
    Object.values(i || {}).some(v => v && String(v).trim() && String(v).trim().toLowerCase() !== 'none')
  );
  if (nonEmpty.length === 0) {
    return <p className="text-sm text-gray-400 italic">No issues reported.</p>;
  }
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            {columns.map(c => <th key={c.key} className="px-2 py-2 font-medium">{c.label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {nonEmpty.map((item, idx) => (
            <tr key={idx}>
              {columns.map(c => (
                <td key={c.key} className="px-2 py-2 align-top text-gray-800">{item[c.key] || '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModuleSection({ n, data }) {
  const ebookRating = data[`module_${n}_ebook_rating`];
  const ebookPositives = data[`module_${n}_ebook_positives`];
  const ebookIssues = data[`module_${n}_ebook_issues`];
  const quizIssues = data[`module_${n}_quiz_issues`];
  const alignmentAnalysis = data[`module_${n}_alignment_analysis`];

  const quizCriteria = [
    ['Content alignment',    data[`module_${n}_quiz_content_alignment`]],
    ['Answer key accuracy',  data[`module_${n}_quiz_answer_key_accuracy`]],
    ['Distractors quality',  data[`module_${n}_quiz_distractors_quality`]],
    ['Question clarity',     data[`module_${n}_quiz_question_clarity`]],
    ['Clinical relevance',   data[`module_${n}_quiz_clinical_relevance`]],
    ['Rationale quality',    data[`module_${n}_quiz_rationale_quality`]],
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-700 rounded-lg flex items-center justify-center text-sm font-bold">
          {n}
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Module {n}</h3>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-800">Ebook</h4>
          <div className="text-xs text-gray-500">Rating: <Rating value={ebookRating} /></div>
        </div>
        {ebookPositives && (
          <div className="mb-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-gray-800">
            <div className="text-xs font-semibold text-emerald-800 mb-1">Positives</div>
            <p className="whitespace-pre-wrap">{ebookPositives}</p>
          </div>
        )}
        <IssuesList
          items={ebookIssues}
          columns={[
            { key: 'location',       label: 'Location' },
            { key: 'issue',          label: 'Issue' },
            { key: 'rationale',      label: 'Rationale' },
            { key: 'recommendation', label: 'Recommendation' },
            { key: 'priority',       label: 'Priority' },
          ]}
        />
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Quiz</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          {quizCriteria.map(([label, value]) => (
            <div key={label} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-sm"><Rating value={value} /></div>
            </div>
          ))}
        </div>
        <IssuesList
          items={quizIssues}
          columns={[
            { key: 'question_number', label: 'Q#' },
            { key: 'issue',           label: 'Issue' },
            { key: 'concern',         label: 'Concern' },
            { key: 'fix',             label: 'Fix' },
            { key: 'priority',        label: 'Priority' },
          ]}
        />
      </div>

      {alignmentAnalysis && (
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-1">Alignment analysis</h4>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{alignmentAnalysis}</p>
        </div>
      )}
    </div>
  );
}

export default function SuperadminCourseReviewsShow({ review }) {
  const d = review.data || {};
  const cross = d.cross_module_analysis || {};
  const finalRec = d.final_recommendation || {};

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link href="/superadmin/course_reviews" className="text-sm text-indigo-600 hover:text-indigo-700">
              ← Back to reviews
            </Link>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              {d.course_title || 'Course Review'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Reviewed by {d.reviewer_name || '—'} on {d.review_date || review.created_at}
            </p>
          </div>
          <span className={`shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${
            review.submitted
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}>
            {review.submitted ? 'Submitted' : 'Draft'}
          </span>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-4">Reviewer</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Name">{d.reviewer_name}</Field>
            <Field label="Credentials">{d.credentials}</Field>
            <Field label="License #">{d.license_number}</Field>
            <Field label="License state">{d.license_state}</Field>
            <Field label="Review date">{d.review_date}</Field>
            <Field label="Signed">{d.reviewer_signature}</Field>
            <Field label="Signature date">{d.signature_date}</Field>
            <Field label="Legacy reviewer_id">
              {review.reviewer_id ? <code className="text-xs">{review.reviewer_id}</code> : null}
            </Field>
          </div>
        </div>

        {[1, 2, 3, 4].map(n => <ModuleSection key={n} n={n} data={d} />)}

        {(cross.content_consistency || cross.progressive_learning) && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cross-module analysis</h3>
            <div className="space-y-4">
              <Field label="Content consistency">
                <p className="whitespace-pre-wrap">{cross.content_consistency}</p>
              </Field>
              <Field label="Progressive learning">
                <p className="whitespace-pre-wrap">{cross.progressive_learning}</p>
              </Field>
            </div>
          </div>
        )}

        {(finalRec.status || (finalRec.priorities || []).some(p => p)) && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Final recommendation</h3>
            <div className="space-y-3">
              <Field label="Status">
                {finalRec.status && (
                  <span className="inline-flex px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-semibold uppercase tracking-wide">
                    {finalRec.status}
                  </span>
                )}
              </Field>
              <Field label="Priority improvements">
                <ol className="list-decimal list-inside space-y-1">
                  {(finalRec.priorities || []).filter(Boolean).map((p, i) => (
                    <li key={i} className="whitespace-pre-wrap">{p}</li>
                  ))}
                </ol>
              </Field>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-400 text-right">
          Created {review.created_at} · Updated {review.updated_at}
        </div>
      </div>
    </Layout>
  );
}
